import { useEffect, useRef, lazy, Suspense } from "react";
import Lenis from "lenis";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import "@/App.css";
import { Toaster } from "@/components/ui/sonner";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import WhatsAppFloat from "@/components/landing/WhatsAppFloat";
import CookieBanner from "@/components/CookieBanner";
import InstallApp from "@/components/InstallApp";
import StickyCtaBar from "@/components/StickyCtaBar";
import { trackEvent } from "@/lib/analytics";
import LandingPage from "@/pages/LandingPage";
import SeoPage from "@/pages/SeoPage";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import Privacy from "@/pages/Privacy";
import Termini from "@/pages/Termini";
import Grazie from "@/pages/Grazie";
import { seoPages } from "@/seo/pages";
import { LanguageProvider } from "@/i18n";

// Admin (recharts, leaflet) caricato solo quando serve: fuori dal primo caricamento pubblico
const Admin = lazy(() => import("@/pages/Admin"));

function Shell() {
  const location = useLocation();
  const navigate = useNavigate();
  const lenisRef = useRef(null);
  const pathRef = useRef(location.pathname);
  pathRef.current = location.pathname;
  const isAdmin = location.pathname.startsWith("/admin");
  const lang =
    location.pathname === "/en" || location.pathname.startsWith("/en/")
      ? "en"
      : location.pathname === "/de" || location.pathname.startsWith("/de/")
        ? "de"
        : "it";

  useEffect(() => {
    // Su dispositivi touch lo scroll nativo è già fluido: Lenis solo su desktop
    const isTouch = window.matchMedia("(pointer: coarse)").matches;
    if (isTouch) return;
    const lenis = new Lenis({ duration: 1.25, smoothWheel: true });
    lenisRef.current = lenis;
    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);

    const onClick = (e) => {
      const anchor = e.target.closest('a[href^="#"], a[href^="/#"]');
      if (!anchor) return;
      e.preventDefault();
      const href = anchor.getAttribute("href");
      const hash = href.startsWith("/#") ? href.slice(1) : href;
      const go = () => {
        const el = document.querySelector(hash);
        if (el) lenis.scrollTo(el, { offset: -64 });
      };
      if (href.startsWith("/#") && pathRef.current !== "/") {
        navigate("/");
        setTimeout(go, 150);
      } else {
        go();
      }
    };
    document.addEventListener("click", onClick);
    return () => {
      cancelAnimationFrame(rafId);
      document.removeEventListener("click", onClick);
      lenis.destroy();
    };
  }, [navigate]);

  useEffect(() => {
    const onTrack = (e) => {
      const anchor = e.target.closest("a");
      if (!anchor) return;
      const href = anchor.getAttribute("href") || "";
      if (href.startsWith("tel:")) trackEvent("click_tel");
      else if (href.includes("wa.me")) trackEvent("click_whatsapp");
    };
    document.addEventListener("click", onTrack);
    return () => document.removeEventListener("click", onTrack);
  }, []);

  useEffect(() => {
    lenisRef.current?.scrollTo(0, { immediate: true });
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <LanguageProvider lang={lang}>
    <div className="min-h-screen bg-[#080808] text-[#E2C882] antialiased overflow-x-hidden">
      <a
        href="#contenuto"
        className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-[80] focus:bg-black focus:gold-frame focus:px-5 focus:py-3 focus:text-sm focus:text-[#F7DE8B] focus:rounded-full"
      >
        Salta al contenuto
      </a>
      <Navbar />
      <main id="contenuto">
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/en" element={<LandingPage />} />
          <Route path="/de" element={<LandingPage />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/en/blog" element={<Blog />} />
          <Route path="/en/blog/:slug" element={<BlogPost />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/termini" element={<Termini />} />
          <Route path="/en/privacy" element={<Privacy />} />
          <Route path="/de/privacy" element={<Privacy />} />
          <Route path="/en/termini" element={<Termini />} />
          <Route path="/de/termini" element={<Termini />} />
          <Route path="/grazie" element={<Grazie />} />
          <Route path="/admin" element={<Suspense fallback={<div className="pt-40 text-center text-[#BFA45D]">Caricamento…</div>}><Admin /></Suspense>} />
          {seoPages.map((p) => (
            <Route key={p.slug} path={p.slug} element={<SeoPage page={p} />} />
          ))}
          <Route path="*" element={<LandingPage />} />
        </Routes>
      </main>
      <Footer />
      <WhatsAppFloat />
      {!isAdmin && <CookieBanner />}
      {!isAdmin && <InstallApp />}
      {!isAdmin && <StickyCtaBar />}
      <Toaster position="top-center" richColors />
    </div>
    </LanguageProvider>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <Shell />
    </BrowserRouter>
  );
}
